import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ImageUploadField from "../../components/admin/ImageUploadField";
import TableScroll from "../../components/TableScroll";
import { fetchCategoryAssessment } from "../../services/assessmentApi";
import { fetchCategories, saveCategory } from "../../services/deviceApi";
import { uploadImage } from "../../services/uploadApi";
import "../../styles/admin-common.css";

const emptyForm = { name: "", logoUrl: "", displayOrder: "", isActive: true };

const Devices = () => {
  const [categories, setCategories] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const previewObjectUrlRef = useRef(null);

  const clearPreviewObjectUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const cats = await fetchCategories();
      setCategories(cats);
      const counts = {};
      await Promise.all(
        cats.map(async (cat) => {
          try {
            const a = await fetchCategoryAssessment(cat.id);
            counts[cat.id] = {
              questions: a.questions?.length || 0,
              defects: a.defects?.length || 0,
            };
          } catch {
            counts[cat.id] = { questions: 0, defects: 0 };
          }
        })
      );
      setAssessments(counts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => clearPreviewObjectUrl();
  }, []);

  const resetForm = () => {
    clearPreviewObjectUrl();
    setPendingFile(null);
    setPreviewUrl("");
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (cat) => {
    clearPreviewObjectUrl();
    setPendingFile(null);
    setPreviewUrl(cat.logoUrl || "");
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      logoUrl: cat.logoUrl || "",
      displayOrder: cat.displayOrder != null ? String(cat.displayOrder) : "",
      isActive: cat.isActive !== false,
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    clearPreviewObjectUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    setPendingFile(file);
    setPreviewUrl(objectUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    let logoUrl = form.logoUrl.trim();
    if (pendingFile) {
      setUploading(true);
      try {
        const uploaded = await uploadImage(pendingFile, "category-images");
        logoUrl = uploaded.url;
      } catch (err) {
        setError(err.message);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    if (!logoUrl) {
      setError("Upload a category image or paste an image URL.");
      return;
    }

    try {
      const payload = {
        name: form.name,
        logoUrl,
        displayOrder: form.displayOrder ? parseInt(form.displayOrder, 10) : 0,
        isActive: form.isActive,
      };
      if (editingId) payload.id = editingId;
      await saveCategory(payload);
      setSuccess(editingId ? "Category updated." : "Category created.");
      resetForm();
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <h2>Device Categories</h2>
      <p className="subtitle">
        Manage product categories (e.g. Mobile, Tablet).{" "}
        <Link to="/catalog">Open catalog hub</Link>
        {" · "}
        <Link to="/questions?scope=category">Question templates</Link>
        {" · "}
        <Link to="/defects?scope=category">Defect templates</Link>
      </p>
      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-card">
        <h3>{editingId ? "Edit Category" : "Add Category"}</h3>
        <form className="admin-form" onSubmit={handleSubmit}>
          <label className="field-label">
            Category name
            <input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <ImageUploadField
            label="Category image"
            imageUrl={form.logoUrl}
            previewUrl={previewUrl}
            pendingFile={pendingFile}
            uploading={uploading}
            folderHint="Shown on home screen · uploads on save"
            onImageUrlChange={(value) => setForm({ ...form, logoUrl: value })}
            onFileSelect={handleFileSelect}
            onClearFile={() => {
              clearPreviewObjectUrl();
              setPendingFile(null);
              setPreviewUrl(form.logoUrl || "");
            }}
          />
          <label className="field-label">
            Display order
            <input type="number" value={form.displayOrder}
              onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} />
          </label>
          <label className="checkbox-field">
            <input type="checkbox" checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            Active
          </label>
          <div className="form-actions">
            <button type="submit" className="admin-btn" disabled={uploading}>
              {uploading ? "Uploading…" : editingId ? "Update" : "Save"}
            </button>
            {editingId && (
              <button type="button" className="admin-btn secondary" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h3>Categories</h3>
        {loading ? <div className="admin-loading">Loading categories</div> : (
          <TableScroll>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Image</th>
                <th>Name</th>
                <th>Order</th>
                <th>Templates</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 && (
                <tr><td colSpan={7} className="empty-msg">No categories</td></tr>
              )}
              {categories.map((cat) => {
                const a = assessments[cat.id] || {};
                const ready = (a.questions || 0) > 0 && (a.defects || 0) > 0;
                return (
                  <tr key={cat.id}>
                    <td>{cat.id}</td>
                    <td>
                      {cat.logoUrl ? (
                        <img src={cat.logoUrl} alt="" className="table-thumb" />
                      ) : "—"}
                    </td>
                    <td><strong>{cat.name}</strong></td>
                    <td>{cat.displayOrder ?? "-"}</td>
                    <td>
                      {a.questions ?? "—"} Q / {a.defects ?? "—"} D
                      {ready && <span className="template-ready"> · Ready</span>}
                    </td>
                    <td className={cat.isActive ? "badge-active" : "badge-inactive"}>
                      {cat.isActive ? "Active" : "Inactive"}
                    </td>
                    <td className="actions-cell">
                      <button type="button" className="admin-btn secondary" onClick={() => startEdit(cat)}>Edit</button>
                      <Link className="admin-btn secondary" to={`/questions?scope=category&categoryId=${cat.id}`}>Questions</Link>
                      <Link className="admin-btn secondary" to={`/defects?scope=category&categoryId=${cat.id}`}>Defects</Link>
                      <Link className="admin-btn" to={`/models?categoryId=${cat.id}`}>Models</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableScroll>
        )}
      </div>
    </div>
  );
};

export default Devices;
